import React, { useState, useEffect, useRef } from 'react';
import { Box } from '@mui/material';

import {
  SlideOutMenuNew,
  TopMenu,
  ReloadButton,
  ErrorBoundary,
  Swipe,
  AviationWeatherAlert,
} from './components';

import { LEFT_DIR, RIGHT_DIR } from './utilities/enums';
import {
  WXG,
  Weather,
  Map,
  NotamNew,
  FplFuel,
  CabinPage,
  PerfCalc,
  CrewInfo,
  CrewMsg,
  CrewBriefingAttachments,
  BriefingTopics,
  StatusPage,
  OFP,
  SeatMap,
  EnRouteWX,
} from './pages';

import { useDataServiceContext } from './hooks/useDataServiceContext';
import { isTrainingMode, setTrainingMode, buildCleaningCheckUrl } from './helpers';

// Library for parsing XML
const parseString = require('xml2js').parseString;

export const Mainapp = ({ empnumber }) => {
  const {
    flightData,
    refreshAllData,
    changeFlight,
    changeAirport,
    changeReqFuel,
    acknowledgeCtotChange,
    acknowledgePreFlightBriefing,
    flightListSyncId,
    reinitialize,
  } = useDataServiceContext();

  const [applicationState, setApplicationState] = useState({
    isCabinCrew: false,
    isTrainingMode: false,
    pageIndex: 0,
      });

  const [isReloading, setIsReloading] = useState(false);
  const [openMenu, setOpenMenu] = useState(null);
  const [statusBriefingOpen, setStatusBriefingOpen] = useState(false);
  const [statusBriefingWasNew, setStatusBriefingWasNew] = useState(false);
  const suppressBriefingAckRef = useRef(false);

  const updateApplicationState = (update) => {
    setApplicationState((prevState) => ({
      ...prevState,
      ...update,
    }));
  };

  useEffect(() => {
    const isCabinCrew = JSON.parse(
      localStorage.getItem('isCabinCrew') ||
        JSON.stringify(applicationState.isCabinCrew)
        );
    const trainingMode = isTrainingMode();
    updateApplicationState({ isCabinCrew, isTrainingMode: trainingMode });
  }, []);

  const handleCabinCrewChange = () => {
    const isCabinCrew = !applicationState.isCabinCrew;
    updateApplicationState({ isCabinCrew, pageIndex: 0 });
    localStorage.setItem('isCabinCrew', JSON.stringify(isCabinCrew));
    refreshAllData();
                };

  const handleTrainingModeChange = async () => {
    const trainingMode = !applicationState.isTrainingMode;
    updateApplicationState({ isTrainingMode: trainingMode, pageIndex: 0 });
    setTrainingMode(trainingMode);
    await reinitialize();
  };

  const handleMenuToggle = (menuType) => {
    setOpenMenu(current => current === menuType ? null : menuType);
  };

  const onSwipe = (dir) => {
    const page = applicationState.pageIndex + 1;
    const pages = getPages().length;
    if (dir === LEFT_DIR) {
      if (page === pages) {
        updateApplicationState({ pageIndex: 0 });
              } else {
        updateApplicationState({ pageIndex: applicationState.pageIndex + 1 });
    }
  }

    if (dir === RIGHT_DIR) {
      if (page === 1) {
        updateApplicationState({ pageIndex: pages - 1 });
        } else {
        updateApplicationState({ pageIndex: applicationState.pageIndex - 1 });
    }
    }
  };

  const getPages = () => {
    let pages;
    // Hide Cabin pages when in training mode
    if (applicationState.isCabinCrew && !applicationState.isTrainingMode) {
      pages = [
        {
          title: 'SEATMAP',
          content: (
            <SeatMap
              passengerList={flightData?.passengerList || []}
              seatmap={flightData?.seatmap || []}
              flight={{
                flightId: flightData.selectedFlightId,
                flightNumber: flightData.selectedFlightId.split('.')[0],
                flightDate: flightData.selectedFlightId.split('.')[1],
                allFlights: flightData.allFlights,
              }}
              dtSTD={flightData?.allData?.dtSTD}
              airportList={
                flightData?.allData?.Airport_List
                  ? flightData?.allData?.Airport_List[0].AirportData
                  : []
              }
              crewList={
                flightData?.allData?.Crew_List
                  ? flightData?.allData?.Crew_List[0].Crew
                  : []
              }
              bagList={flightData?.bagList || { checkedInCount: 0, loadedCount: 0, bags: [] }}
            />
          ),
        },
        {
          title: 'CABIN INFO TXT FORMAT',
          content: (
            <CabinPage
              data-private
              data={flightData.allData}
              cifData={flightData.cif}
            />
          ),
        },
        {
          title: 'MAP',
          content: (
            <Map
              data={flightData.allData}
              reqFuel={flightData.reqFuel}
              isCabinCrew={true}
              parseString={parseString}
              isTrainingMode={applicationState.isTrainingMode}
            />
          ),
        },
        {
          title: 'CREW MSG',
          content: <CrewMsg data={flightData.allData} />,
        },
        {
          title: 'CREW BRIEFING ATTACHMENTS',
          content: (
            <CrewBriefingAttachments
              flightId={flightData?.selectedFlightId || ''}
            />
          ),
        },
        {
          id: 'pre-flight-briefing',
          title: 'PRE-FLIGHT CREW BRIEFING',
          isNew: flightData.preFlightBriefingIsNew,
          content: <BriefingTopics topic={flightData.preFlightBriefing} />,
        },
      ];
    } else {
      pages = [
        {
          title: 'FPL+FUEL',
          content: (
        <FplFuel
              data={flightData.allData}
              airports={flightData.airports}
              taf={flightData.taf}
              metar={flightData.metar}
              snowtam={flightData.snowtam}
              airportSelected={flightData.airportSelected}
              onChangeAirport={changeAirport}
              reqFuel={flightData.reqFuel}
              onChangeReqFuel={changeReqFuel}
              tkofSettings={flightData.tkofSettings}
              coldSoak={flightData.coldSoak}
              adjustments={flightData?.allData?.sAdjustments[0] ?? ''}
        />
          ),
        },
        {
          title: 'AD WX+NTM+RAIM+SPACE WX',
          content: (
        <Weather
              data={flightData.allData}
              airports={flightData.airports}
              metar={flightData.metar}
              taf={flightData.taf}
              snowtam={flightData.snowtam}
              airportSelected={flightData.airportSelected}
              onChangeAirport={changeAirport}
              reqFuel={flightData.reqFuel}
        />
          ),
        },
        ...(applicationState.isTrainingMode
          ? [
            {
              title: 'ENROUTE WX',
              content: (
                <EnRouteWX
                  key={`enroute-wx-${applicationState.isTrainingMode}-${flightData.selectedFlightId}-${flightData.dataFetchTimestamp}`}
                  data={flightData.allData}
                  trainingMode={applicationState.isTrainingMode}
                />
              ),
            },
          ]
          : []),
        {
          title: 'MAP',
          content: (
        <Map
              data={flightData.allData}
              reqFuel={flightData.reqFuel}
              areaMessages={flightData.areaMessages}
              isCabinCrew={false}
              parseString={parseString}
              isTrainingMode={applicationState.isTrainingMode}
        />
          ),
        },
        {
          title: 'ENR NOTAM',
          content: <NotamNew data={flightData.allData} />,
        },
        {
          title: 'WXG',
          content: (
            <WXG
              data={{
                ...flightData.allData,
                wxgImages: flightData?.wxgImages ?? [],
              }}
            />
          ),
        },
        {
          title: 'PERFORMANCE CALC',
          content: (
        <PerfCalc
              data={flightData.allData}
          parseString={parseString}
              empNumber={empnumber}
              reqFuel={flightData.reqFuel}
              tkofSettings={flightData.tkofSettings}
        />
          ),
      },
      {
          title: 'CREW INFO',
          content: (
            <CrewInfo
              dtSTD={flightData?.allData?.dtSTD}
              airportList={
                flightData?.allData?.Airport_List
                  ? flightData?.allData?.Airport_List[0].AirportData
                  : []
              }
              crewList={
                flightData?.allData?.Crew_List
                  ? flightData?.allData?.Crew_List[0].Crew
                  : []
              }
            />
          ),
      },
      {
        title: 'CREW MSG',
          content: <CrewMsg data={flightData.allData} />,
      },
        {
          title: 'CREW BRIEFING ATTACHMENTS',
          content: (
            <CrewBriefingAttachments
              flightId={flightData?.selectedFlightId || ''}
            />
          ),
        },
        {
          id: 'pre-flight-briefing',
          title: 'PRE-FLIGHT CREW BRIEFING',
          isNew: flightData.preFlightBriefingIsNew,
          content: <BriefingTopics topic={flightData.preFlightBriefing} />,
        },
        {
          title: 'OFP',
          content: <OFP flightId={flightData?.selectedFlightId || ''} />,
        },
        // Hide SEATMAP page when in training mode
        ...(applicationState.isTrainingMode ? [] : [
        {
          title: 'SEATMAP',
          content: (
            <SeatMap
              passengerList={flightData?.passengerList || []}
              seatmap={flightData?.seatmap || []}
              flight={{
                flightId: flightData.selectedFlightId,
                flightNumber: flightData.selectedFlightId.split('.')[0],
                flightDate: flightData.selectedFlightId.split('.')[1],
                allFlights: flightData.allFlights,
              }}
              dtSTD={flightData?.allData?.dtSTD}
              airportList={
                flightData?.allData?.Airport_List
                  ? flightData?.allData?.Airport_List[0].AirportData
                  : []
              }
              crewList={
                flightData?.allData?.Crew_List
                  ? flightData?.allData?.Crew_List[0].Crew
                  : []
              }
              bagList={flightData?.bagList || { checkedInCount: 0, loadedCount: 0, bags: [] }}
            />
          ),
        },
        ]),
    ];

    }
    return pages;
  };

  const handleReloadClick = () => {
    if (!isReloading) {
      setIsReloading(true);
      refreshAllData();
      setTimeout(() => {
        setIsReloading(false);
      }, 30000);
    }
  };

  useEffect(() => {
    if (flightData.status) {
      // The status screen covers the page. Uncovering it later is not an open.
      suppressBriefingAckRef.current = true;
      return;
    }

    const onBriefing =
      getPages()[applicationState.pageIndex]?.id === 'pre-flight-briefing';
    if (!onBriefing || !flightData.preFlightBriefingIsNew) {
      suppressBriefingAckRef.current = false;
      return;
    }

    if (suppressBriefingAckRef.current) {
      suppressBriefingAckRef.current = false;
      return;
    }

    acknowledgePreFlightBriefing();
  }, [
    applicationState.pageIndex,
    applicationState.isCabinCrew,
    applicationState.isTrainingMode,
    flightData.status,
    flightData.preFlightBriefingIsNew,
    flightListSyncId,
  ]);

  const openNoFlightBriefing = () => {
    setStatusBriefingWasNew(!!flightData.preFlightBriefingIsNew);
    setStatusBriefingOpen(true);
    acknowledgePreFlightBriefing();
  };

  if (flightData.status) {
    return (
      <StatusPage
        status={flightData.status}
        onTrainingModeChange={handleTrainingModeChange}
        briefing={flightData.preFlightBriefing}
        briefingIsNew={flightData.preFlightBriefingIsNew}
        briefingOpen={statusBriefingOpen}
        briefingWasNew={statusBriefingWasNew}
        onOpenBriefing={openNoFlightBriefing}
        onCloseBriefing={() => setStatusBriefingOpen(false)}
      />
    );
  }

  return (
    <div className='App'>
            <ErrorBoundary
              customErrorMessage={
                'An error occured in the slide out menu title "information".'
              }
            >
        <SlideOutMenuNew
          side='left'
          title='information'
          backgroundColor='#D81939'
          height='33vh'
          maxHeight='17rem'
          topPosition='35%'
          activeIndex={applicationState.pageIndex}
          navigate={(index) => updateApplicationState({ pageIndex: index })}
          menuItems={getPages()}
          openMenu={openMenu}
          setOpenMenu={setOpenMenu}
          onMenuToggle={handleMenuToggle}
          cleaningCheckUrl={buildCleaningCheckUrl({
            isCabinCrew: applicationState.isCabinCrew,
            departureAirport: flightData?.allData?.sDepIATA || '',
            flightDate: flightData.selectedFlightId?.split('.')[1] || '',
            flightNumber: flightData.selectedFlightId?.split('.')[0] || '',
            aircraftRegistration: flightData?.allData?.sACReg || '',
          })}
          cleaningCheckDisabled={!navigator.onLine}
            />
            </ErrorBoundary>

            <ErrorBoundary
              customErrorMessage={
                'An error occured in the slide out menu title "flights".'
              }
            >

        <SlideOutMenuNew
          side='left'
          title='flights'
          backgroundColor='#003D50'
          height='25vh'
          maxHeight='11rem'
          topPosition='2%'
          menuItems={flightData.allFlights}
              flightMenu={true}
          changeFlight={changeFlight}
          changeCrewType={handleCabinCrewChange}
          isCabinCrew={applicationState.isCabinCrew}
          isTrainingMode={applicationState.isTrainingMode}
          onTrainingModeChange={handleTrainingModeChange}
          selectedFlightId={flightData.selectedFlightId}
          openMenu={openMenu}
          setOpenMenu={setOpenMenu}
          onMenuToggle={handleMenuToggle}
            />
            </ErrorBoundary>

            {navigator.onLine && (
              <Box className={'reload-button-container'}>
          <ReloadButton onClick={handleReloadClick} disabled={isReloading} />
              </Box>
            )}

      <Swipe onSwipe={onSwipe}>
        <ErrorBoundary customErrorMessage={'An error occured in the top menu.'}>
            <TopMenu
            data={flightData.allData}
            dataFetchTimestamp={flightData.dataFetchTimestamp}
            pageTitle={getPages()[applicationState.pageIndex]?.title}
            navigateToMap={() => updateApplicationState({ pageIndex: 2 })}
            ctot={flightData.ctot}
            ctotCrew={flightData.ctotCrew}
            ctotHasChanged={flightData.ctotHasChanged}
            onCtotAcknowledge={() => acknowledgeCtotChange(flightData.selectedFlightId)}
            nbrOfFlights={flightData.allFlights.length}
            isCabinCrew={applicationState.isCabinCrew}
            flightInfoCrew={flightData.flightInfoCrew}
            changeFlight={changeFlight}
            />
            </ErrorBoundary>
            <ErrorBoundary
          customErrorMessage={`An error occured in the page ${getPages()[applicationState.pageIndex]?.title
          }`}
            >
          {getPages()[applicationState.pageIndex]?.content}
            </ErrorBoundary>
            </Swipe>

      <AviationWeatherAlert />
      </div>
    );
};
